import React, { useCallback } from 'react'
import { t, Trans } from '@lingui/macro'
import * as Yup from 'yup'
import {
  ButtonClean,
  ButtonPrimary,
  Modal,
  FormLabel,
  Input,
  Flex,
  DatePicker,
  Textarea,
  ButtonOutline,
  Tooltip,
  FormErrorMessage,
  Box,
  FormControl
} from 'front-end/src/components'
import dayjs from 'shared/dayjs'
import { useFormik } from 'formik'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'
import { useDropzone, ErrorCode } from 'react-dropzone'
import IconUpload from 'front-end/src/components/common/icon/IconUpload'
import { TooltipHelpInfo } from 'front-end/src/components/common/tooltip/TooltipHelp'
import { css } from '@emotion/css'
import { getDateFormatBasedOnLocale } from 'front-end/src/components/common/date-time-pickers/dateTimePickersUtils'
import {
  useAddCpdEntryMutation,
  usePatchCpdEntryMutation
} from 'front-end/src/pages/activity-history/ActivityHistory.codegen'
import {
  CpdManualEntryModelInput,
  Maybe
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { LooopS3FileUpload } from 'front-end/src/app/util/LooopS3FileUpload'
import { LooopS3BucketEnum } from 'types/UploadCredentials'
import {
  CPDManualFormValues,
  CPDManualFormValuesWithId
} from 'front-end/src/pages/activity-history/activityHistoryTypes'

interface ActivityHistoryCPDManualModalProps {
  isOpen: boolean
  onClose: () => void
  editData: CPDManualFormValuesWithId | null
  clearEditData: () => void
  userId?: number
  refetch: () => Promise<any>
}

const SUPPORTED_FILE_TYPES = [
  'image/*',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.pdf',
  '.csv'
]
const FILE_MEGABYTES_COUNT = 10
const FILE_LIMIT = 1024 * 1024 * FILE_MEGABYTES_COUNT
const MAX_FILES_LIMIT = 1
const RESET_FORM_DELAY = 1000
const DATE_FORMAT_TO_SAVE = 'YYYY-MM-DD'

let restTimer: NodeJS.Timeout

const ActivityHistoryCPDManualModal = ({
  isOpen,
  onClose,
  editData,
  userId,
  clearEditData,
  refetch
}: ActivityHistoryCPDManualModalProps) => {
  const { theme } = useLooopTheme()
  const formatBasedOnLocale = getDateFormatBasedOnLocale()
  const validationSchema = Yup.object({
    activityName: Yup.string().required(t`Name is required`),
    durationHours: Yup.number()
      .min(0, t`Duration hours should be greater or equals 0`)
      .required(t`Duration hours is required`),
    durationMinutes: Yup.number()
      .when('durationHours', {
        is: (val) => +val <= 0,
        then: Yup.number()
          .min(1, t`Duration minutes should be 1-59`)
          .max(59, t`Duration minutes should be 1-59`),
        otherwise: Yup.number()
          .min(0, t`Duration minutes should be 0-59`)
          .max(59, t`Duration minutes should be 0-59`)
      })
      .required(t`Duration minutes is required`)
  })
  const handleAddOrUpdate = async (formValues: CPDManualFormValues) => {
    let cdnUrl: Maybe<string> | undefined = ''

    const input: CpdManualEntryModelInput = {
      name: formValues.activityName,
      description: formValues.description,
      source: formValues.source,
      number_of_minutes:
        formValues.durationHours * 60 + formValues.durationMinutes,
      date_started: dayjs(formValues.dateStarted, formatBasedOnLocale).format(
        DATE_FORMAT_TO_SAVE
      ),
      date_completed: dayjs(
        formValues.dateCompleted,
        formatBasedOnLocale
      ).format(DATE_FORMAT_TO_SAVE)
    }

    if (formValues.attachment) {
      cdnUrl = await LooopS3FileUpload({
        S3UploadProps: {
          filename: formValues.attachment.name,
          S3path: `cpd/user-${userId}`,
          bucket: LooopS3BucketEnum.PRIVATE
        },
        blob: formValues.attachment
      })
    }
    const cdnUrlData = cdnUrl ? { document_cdn_url: cdnUrl } : {}
    const payload = {
      ...input,
      ...cdnUrlData
    }

    if (editData) {
      await patchCPDEntry({
        variables: {
          input: payload,
          userId: userId,
          id: editData.id
        }
      })
    } else {
      await createCPDEntry({
        variables: {
          input: payload
        }
      })
    }
  }
  const {
    values,
    handleSubmit,
    setFieldValue,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldError,
    resetForm,
    isSubmitting
  } = useFormik<CPDManualFormValues>({
    initialValues: {
      activityName: editData?.activityName || '',
      durationHours: editData?.durationHours || 0,
      durationMinutes: editData?.durationMinutes || 0,
      source: editData?.source || '',
      dateStarted: dayjs(editData?.dateStarted).format(formatBasedOnLocale),
      dateCompleted: dayjs(editData?.dateCompleted).format(formatBasedOnLocale),
      description: editData?.description || '',
      attachment: null
    },
    onSubmit: handleAddOrUpdate,
    validationSchema,
    enableReinitialize: true
  })

  const handleHide = () => {
    onClose()

    if (restTimer) {
      clearTimeout(restTimer)
    }

    restTimer = setTimeout(() => {
      clearEditData()
      resetForm()
    }, RESET_FORM_DELAY)
  }

  const [createCPDEntry] = useAddCpdEntryMutation({
    onCompleted: () => {
      refetch()
      handleHide()
    },
    onError: handleHide
  })
  const [patchCPDEntry] = usePatchCpdEntryMutation({
    onCompleted: () => {
      refetch()
      handleHide()
    },
    onError: handleHide
  })

  const onDropRejected = useCallback((fileRejections) => {
    const { errors } = fileRejections[0]
    const isFileTypeError = errors.some(
      (error) => error.code === ErrorCode.FileInvalidType
    )
    const errorMessage = isFileTypeError
      ? t`This file type is not permitted`
      : t`This file size exceeds the limit`

    setFieldError('attachment', errorMessage)
  }, [])

  const onDropAccepted = useCallback((acceptedFiles) => {
    setFieldValue('attachment', acceptedFiles[0])
  }, [])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: SUPPORTED_FILE_TYPES,
    maxSize: FILE_LIMIT,
    onDropRejected,
    onDropAccepted,
    noClick: true,
    maxFiles: MAX_FILES_LIMIT
  })

  const isEditMode = editData !== null
  const headerTitle = isEditMode
    ? t`Edit external activity`
    : t`Add external activity`
  const submitButtonText = isEditMode ? t`Save` : t`Add activity`
  const isDurationInvalid = !!(
    (touched.durationMinutes || touched.durationHours) &&
    (errors.durationHours || errors.durationMinutes)
  )
  const isNameInvalid = touched.activityName && !!errors.activityName
  const fileName = values.attachment?.name || editData?.fileName

  const regex = /(.*)1[0-9]{12}\.(.*)/gi
  const fileNameWithouTimestamp = fileName?.replace(regex, '$1.$2') || ''

  return (
    <Modal
      header={headerTitle}
      onHide={handleHide}
      show={isOpen}
      newDesign
      controls={[
        <ButtonClean key={1} onClick={handleHide}>
          <Trans>Cancel</Trans>
        </ButtonClean>,
        <ButtonPrimary
          key={2}
          onClick={() => {
            handleSubmit()
          }}
          isLoading={isSubmitting}
        >
          {submitButtonText}
        </ButtonPrimary>
      ]}
      className={css`
        .Modal__header {
          padding-top: 24px;
          padding-bottom: 4px;
        }
      `}
    >
      <form onSubmit={handleSubmit}>
        <FormControl isInvalid={isNameInvalid}>
          <FormLabel htmlFor="cpdActivityName">
            <Trans>Activity name</Trans>
          </FormLabel>
          <Input
            value={values.activityName}
            name="activityName"
            onChange={handleChange}
            onBlur={handleBlur}
            id="cpdActivityName"
            variant="filled"
          />
          <FormErrorMessage>{errors.activityName}</FormErrorMessage>
        </FormControl>
        <Flex justifyContent="space-between">
          <Box width="calc(50% - 16px)">
            <FormControl isInvalid={isDurationInvalid}>
              <FormLabel htmlFor="cpdActivityHours" display="inline-block">
                <Trans>Duration</Trans>
              </FormLabel>
              <Flex alignItems="center">
                <Input
                  id="cpdActivityHours"
                  name="durationHours"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.durationHours}
                  type="number"
                  variant="filled"
                  min={0}
                  isInvalid={!!errors.durationHours}
                />
                <Box fontSize="14px" color={theme.colors.grey['500']} m="0 8px">
                  <Trans>hours</Trans>
                </Box>
                <Input
                  value={values.durationMinutes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="durationMinutes"
                  variant="filled"
                  type="number"
                  min={0}
                />
                <Box fontSize="14px" color={theme.colors.grey['500']} ml="8px">
                  <Trans>min</Trans>
                </Box>
              </Flex>
              <FormErrorMessage>
                {errors.durationHours || errors.durationMinutes}
              </FormErrorMessage>
            </FormControl>
          </Box>
          <Box width="calc(50% - 16px)">
            <FormLabel htmlFor="cpdActivitySource">
              <Trans>Source (optional)</Trans>
            </FormLabel>
            <Input
              id="cpdActivitySource"
              value={values.source}
              name="source"
              onChange={handleChange}
              variant="filled"
            />
          </Box>
        </Flex>
        <Flex justifyContent="space-between">
          <Box width="calc(50% - 16px)">
            <FormLabel>
              <Trans>Date started</Trans>
            </FormLabel>
            <DatePicker
              value={values.dateStarted}
              onChange={(value) => {
                const newValue = dayjs(value?.toString())
                const completedDate = dayjs(
                  values.dateCompleted,
                  formatBasedOnLocale
                )

                setFieldValue(
                  'dateStarted',
                  newValue.format(formatBasedOnLocale)
                )

                if (newValue.isAfter(completedDate)) {
                  setFieldValue(
                    'dateCompleted',
                    newValue.format(formatBasedOnLocale)
                  )
                }
              }}
            />
          </Box>
          <Box width="calc(50% - 16px)">
            <FormLabel>
              <Trans>Date completed</Trans>
            </FormLabel>
            <DatePicker
              value={values.dateCompleted}
              onChange={(value) => {
                const newValue = dayjs(value?.toString())
                const startedDate = dayjs(
                  values.dateStarted,
                  formatBasedOnLocale
                )

                setFieldValue(
                  'dateCompleted',
                  newValue.format(formatBasedOnLocale)
                )

                if (newValue.isBefore(startedDate)) {
                  setFieldValue(
                    'dateStarted',
                    newValue.format(formatBasedOnLocale)
                  )
                }
              }}
            />
          </Box>
        </Flex>
        <FormLabel htmlFor="cpdActivityDescription" display="inline-block">
          <Trans>Description (optional)</Trans>
        </FormLabel>
        <Textarea
          value={values.description}
          name="description"
          onChange={handleChange}
          variant="filled"
          id="cpdActivityDescription"
          display="block"
          resize="vertical"
        />
        <FormLabel htmlFor="cpdActivityFile" display="inline-block">
          <Trans>Upload attachment (optional)</Trans>
        </FormLabel>
        <Box
          {...getRootProps()}
          border="1px dashed"
          borderColor={
            isDragActive ? theme.colors.teal['500'] : theme.colors.grey.light
          }
          borderRadius={theme.radii.lg}
          p="12px 16px"
        >
          <Box color={theme.colors.grey['500']} mb="8px">
            <Trans>Drag your file here or</Trans>
          </Box>
          <Flex alignItems="center">
            <input {...getInputProps()} />
            <ButtonOutline
              onClick={open}
              leftIcon={<IconUpload />}
              mr="8px"
              pl="12px"
              pr="12px"
            >
              <Trans>Select file to upload</Trans>
            </ButtonOutline>
            <Box
              width="100%"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {!errors.attachment && (
                <Tooltip text={fileNameWithouTimestamp}>
                  {fileNameWithouTimestamp}
                </Tooltip>
              )}
              {errors.attachment && (
                <Box color={theme.colors.red['500']} fontSize="12px">
                  {errors.attachment}
                </Box>
              )}
            </Box>
          </Flex>
          <Flex mt="8px" fontSize="12px" color={theme.colors.grey['400']}>
            <Trans>
              You can upload documents or images up to {FILE_MEGABYTES_COUNT}MB
            </Trans>
            <TooltipHelpInfo isBlock>
              <Trans>
                Accepted file types: jpg, jpeg, png, gif, svg, pdf, doc, docx,
                xls, xlsx, ppt, pptx, csv
              </Trans>
            </TooltipHelpInfo>
          </Flex>
        </Box>
      </form>
    </Modal>
  )
}

export default React.memo(ActivityHistoryCPDManualModal)
